import {useNotification} from '../hooks/use-notifications.tsx';
import {createForm} from '../forms';
import {createUserSchema, CreateUserSchema} from './schemas.ts';
import {useCallback, useMemo} from 'react';
import {DefaultValues, SubmitHandler, useFormContext} from 'react-hook-form';
import {UsersService} from '../api/generated/UsersService.ts';
import {Link, useNavigate} from 'react-router-dom';
import {ROUTES} from '../routes';
import {BasicPage} from '../components/basic-page.tsx';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {Button, Group, Stack, Text} from '@mantine/core';
import {RequireAuthorization} from '../auth/require-authorization.tsx';
import {AllPermissions, Roles, UserTitles} from '../api/generated/enums.ts';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncFn} from 'react-use';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {DeleteButton} from '../components/delete-button.tsx';
import {PartnerSitesService} from '../api/generated/PartnerSitesService.ts';
import {useOptions} from '../hooks/use-options.ts';

const Form = createForm<CreateUserSchema>();

const DEFAULT_VALUES = {
  role: Roles['System User'] as Roles,
  inactive: false,
};

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Users', href: ROUTES.ADMIN.USERS.INDEX.path},
  {title: 'Create'},
];

export const UserCreate = () => {
  const notification = useNotification();
  const navigate = useNavigate();

  const onSubmit = useCallback<SubmitHandler<CreateUserSchema>>(
    async (values) => {
      const response = await UsersService.create({body: values});
      if (response.hasErrors) {
        return response;
      }

      notification.success('User Created');
      navigate(ROUTES.ADMIN.USERS.INDEX.path);
    },
    [navigate, notification],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form
        schema={createUserSchema}
        onSubmit={onSubmit}
        defaultValues={DEFAULT_VALUES}
      >
        <Form.Container>
          <Form.ServerError />
          <FormFields />
          <Group mt={24} justify={'flex-end'}>
            <Button
              type='button'
              variant='outline'
              component={Link}
              to={ROUTES.ADMIN.USERS.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create User</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const UserDetails = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.USERS.DETAIL);
  const navigate = useNavigate();
  const notifications = useNotification();

  const fetchUser = useAsync(async () => {
    const {data} = await UsersService.getById({
      id,
    });
    return data;
  }, [id]);

  const user = fetchUser.value || undefined;
  const userName = user ? `${user.firstName} ${user.lastName}` : '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Users', href: ROUTES.ADMIN.USERS.INDEX.path},
      {title: userName},
    ],
    [userName],
  );

  const onSubmit = useCallback<SubmitHandler<CreateUserSchema>>(
    async (values) => {
      const response = await UsersService.update({id, body: values});
      if (response.hasErrors) {
        return response;
      }
      notifications.success('User Updated');
      navigate(ROUTES.ADMIN.USERS.INDEX.path);
    },
    [id, navigate, notifications],
  );

  const [deleteUserState, deleteUser] = useAsyncFn(async () => {
    const response = await UsersService.deleteById({id});

    if (response.hasErrors) {
      notifications.error('Unable to delete user');
      return;
    }

    notifications.success('User successfully deleted');
    navigate(ROUTES.ADMIN.USERS.INDEX.path);
  }, [id, navigate, notifications]);

  const defaultValues = useMemo<DefaultValues<CreateUserSchema>>(
    () => ({
      ...user,
      partnerSiteIds: user?.partnerSites?.map(x => x.id),
      role: user?.role as Roles,
      title: user?.title === '' ? undefined : (user?.title as UserTitles),
    }),
    [user],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchUser}>
        {user && (
          <Form
            defaultValues={defaultValues}
            schema={createUserSchema}
            onSubmit={onSubmit}
            debug
          >
            <Form.Container>
              <FormFields />
              <Group mt={24} justify={'space-between'}>
                <DeleteButton
                  buttonLabel={'Delete User'}
                  confirmButtonLabel={'Delete User'}
                  onConfirm={deleteUser}
                  loading={deleteUserState.loading}
                  confirmTitle={
                    <Text fw={600}>
                      Are you sure you want to delete this user?
                    </Text>
                  }
                />
                <Group>
                  <Button
                    type='button'
                    variant='outline'
                    component={Link}
                    to={ROUTES.ADMIN.USERS.INDEX.path}
                  >
                    Cancel
                  </Button>
                  <Form.Submit>Update User</Form.Submit>
                </Group>
              </Group>
            </Form.Container>
          </Form>
        )}
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  const partnerSiteOptions =
    useOptions(PartnerSitesService.getOptions).value ?? [];

  const {watch} = useFormContext<CreateUserSchema>();
  const isRoleSiteManager = watch('role') === Roles['Partner Site Manager'];

  return (
    <Stack>
      <Form.Row>
        <Form.Select
          name='title'
          label='Title'
          enumObject={UserTitles}
          clearable
        />
        <Form.TextInput
          name='firstName'
          label='First Name'
          placeholder='First Name'
        />
        <Form.TextInput
          name='lastName'
          label='Last Name'
          placeholder='Last Name'
        />
      </Form.Row>
      <Form.Row>
        <Form.TextInput
          name='emailAddress'
          label='Email Address'
          placeholder='Email Address'
        />
        <RequireAuthorization role='Global Admin'>
          <Form.Select
            name='role'
            enumObject={Roles}
            label='Role'
            placeholder='Role'
          />
        </RequireAuthorization>
        <RequireAuthorization role='Global Admin'>
          {isRoleSiteManager && (
            <Form.MultiSelect
              name='claims'
              enumObject={AllPermissions}
              label='Claims'
              placeholder='Claims'
            />
          )}
        </RequireAuthorization>
      </Form.Row>
      <Form.Row>
        <Form.MultiSelect
          data={partnerSiteOptions}
          label='Partner Sites'
          name='partnerSiteIds'
        />
        <Form.Checkbox name='inactive' label='Inactive' />
      </Form.Row>
    </Stack>
  );
};